import React from 'react';
import { Calendar, Sun, Moon, Coffee } from 'lucide-react';
import { SEASONS } from '../../utils/constants';
import { LifeStats } from '../../hooks/useLifeStats';
import Card from '../Card';
import ProgressBar from '../ProgressBar';
import StatDisplay from '../StatDisplay';
import FunctionHealthMiniCTA from '../FunctionHealthMiniCTA';

interface ProgressViewProps {
  stats: LifeStats;
}

const ProgressView: React.FC<ProgressViewProps> = ({ stats }) => {
  if (stats.lifeExpectancy <= 0) return null;
  
  // Find current season
  const currentSeason = SEASONS.find(
    season => stats.currentYear >= season.start && stats.currentYear <= season.end
  );
  
  return (
    <div className="w-full max-w-4xl">
      <Card className="mb-6 p-6" animated={true}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Life Progress</h2>
          <p className="text-gray-600">
            You've lived approximately {stats.progressPercent.toFixed(1)}% of your statistical life expectancy.
          </p>
        </div>
        
        <div className="h-8 mb-8">
          <ProgressBar 
            value={stats.yearsLived} 
            maxValue={stats.lifeExpectancy} 
            label="Life Journey" 
            height="h-8"
            animate={true}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold mb-4 text-gray-700">Time Metrics</h3>
            <div className="space-y-3">
              <StatDisplay 
                icon={Calendar} 
                value={`${stats.yearsLived.toFixed(1)} of ${stats.lifeExpectancy} years`}
                label="Years Lived" 
                animated={true}
              />
              <StatDisplay 
                icon={Calendar} 
                value={`${stats.monthsLived.toLocaleString()} months`}
                label="Months Lived"
                color="green" 
                animated={true}
              />
              <StatDisplay 
                icon={Calendar} 
                value={`${stats.weeksLived.toLocaleString()} weeks`}
                label="Weeks Lived"
                color="purple" 
                animated={true}
              />
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-4 text-gray-700">Life Experience</h3>
            <div className="space-y-3">
              <StatDisplay 
                icon={Sun} 
                value={stats.sunrisesSeen.toLocaleString()}
                label="Sunrises Seen" 
                color="yellow"
                animated={true}
              />
              <StatDisplay 
                icon={Moon} 
                value={stats.moonPhasesLived.toLocaleString()}
                label="Moon Phases Observed" 
                color="purple"
                animated={true}
              />
              <StatDisplay 
                icon={Coffee} 
                value={stats.mealsEaten.toLocaleString()}
                label="Meals Enjoyed" 
                color="orange"
                animated={true}
              />
            </div>
          </div>
        </div>
        
        {currentSeason && (
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-full bg-${currentSeason.color}-100 text-${currentSeason.color}-500`}>
                <currentSeason.icon size={24} />
              </div>
              <div>
                <h3 className="font-semibold">Current Season: {currentSeason.name}</h3>
                <p className="text-sm text-gray-600">{currentSeason.description}</p>
              </div>
            </div>
          </div>
        )}

        {/* Health tracking nudge */}
        <FunctionHealthMiniCTA
          className="mt-8"
          message="Track your health progress with real data"
          buttonText="Explore"
        />
      </Card>
    </div>
  );
};

export default ProgressView; 